// useIdleLogout.js
import { useEffect, useRef } from "react";

/**
 * @param {Object} params
 * @param {number} params.timeoutMs - 무활동 제한 시간(ms). 예: 30분 = 30*60*1000
 * @param {boolean} params.enabled - 로그인 상태일 때만 동작
 * @param {Function} params.onLogout - 로그아웃 실행 함수 (dispatch(setLogout()) 등)
 * @param {string} [params.storageKey] - 여러 탭 동기화용 키
 */
export function useIdleLogout({
  timeoutMs,
  enabled,
  onLogout,
  storageKey = "lastActivityAt",
}) {
  const timerRef = useRef(null);
  const firedRef = useRef(false);

  const clear = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const schedule = () => {
    clear();
    timerRef.current = setTimeout(() => {
      if (firedRef.current) return;
      firedRef.current = true;
      onLogout?.();
    }, timeoutMs);
  };

  const touch = () => {
    if (!enabled) return;

    firedRef.current = false;
    const now = Date.now();
    localStorage.setItem(storageKey, String(now));
    schedule();
  };

  useEffect(() => {
    if (!enabled) return;

    // 최초 시작
    const last = Number(localStorage.getItem(storageKey) || Date.now());
    const elapsed = Date.now() - last;

    if (elapsed >= timeoutMs) {
      // 이미 무활동 시간 초과 상태면 즉시 로그아웃
      firedRef.current = true;
      onLogout?.();
      return;
    } else {
      // 남은 시간만큼 타이머 설정
      clear();
      timerRef.current = setTimeout(() => {
        if (firedRef.current) return;
        firedRef.current = true;
        onLogout?.();
      }, timeoutMs - elapsed);
    }

    // 활동 이벤트
    const events = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "pointerdown",
      "visibilitychange",
    ];

    const handler = () => {
      // 탭이 다시 보이는 순간도 활동으로 간주할지 여부는 취향인데, 보통 갱신해줌
      if (document.visibilityState === "hidden") return;
      touch();
    };

    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));

    // 다른 탭에서 활동하면 같이 연장되도록 동기화
    const onStorage = (e) => {
      if (e.key !== storageKey) return;
      if (!enabled) return;
      firedRef.current = false;
      schedule();
    };
    window.addEventListener("storage", onStorage);

    return () => {
      clear();
      events.forEach((e) => window.removeEventListener(e, handler));
      window.removeEventListener("storage", onStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, timeoutMs]);

  // enabled가 true로 바뀌는 순간(로그인 직후)에도 한번 찍어주는 게 안전
  useEffect(() => {
    if (enabled) {
      localStorage.setItem(storageKey, String(Date.now()));
    } else {
      clear();
      firedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
}
